// react
import React, { useEffect, useState } from "react";
// reactstrap
import { Collapse, Modal, ModalBody, ModalHeader, Spinner } from "reactstrap";
// service
import {
  getAllForms,
  getUserResponse,
  createUserResponse,
} from "../../../utils/services/rsvp";
import { getUrlFiles } from "../../../utils/services/files";
// components
import FormMounted from "../rsvp/FormMounted";
import SectionComponent from "../../Apps/Personalization/SectionComponent";
// alert
import SweetAlert from "react-bootstrap-sweetalert";

// Tipos de input que não devem ter valor persistido nem enviado à API
const IGNORED_INPUT_TYPES = ["Botao de envio"];

// Tipos de input que devem ser exibidos, mas desabilitados para edição quando o participante já respondeu
const LOCKED_INPUT_TYPES = ["Email"];

// Remove obrigatoriedade de todos os campos — regra exclusiva deste modal
function stripRequired(rows) {
  return rows.map((row) => ({
    ...row,
    cols: row.cols.map((col) => ({
      ...col,
      attributes: { ...col.attributes, required: false },
    })),
  }));
}

// Renderiza um formulário individual com estado próprio
function FormItem({ formData, participant, toggle, isOpen }) {
  const [form, setForm] = useState([]);
  // undefined = aguardando resposta da API; [] ou [...] = já carregado
  const [savedResponses, setSavedResponses] = useState(undefined);
  // alertas
  const [successAlert, setSuccessAlert] = useState(false);
  const [errorAlert, setErrorAlert] = useState(false);

  async function handleSubmit() {
    try {
      // Upload de arquivos: substitui o value pelo URL antes de montar formColumns
      for (const row of form) {
        for (const col of row.cols) {
          const val = col.attributes?.value;
          const isFileField =
            val && typeof val === "object" && val.files && val.files.length > 0;
          if (isFileField) {
            const fd = new FormData();
            for (const f of val.files) fd.append("files", f);
            const uploadRes = await getUrlFiles(fd);
            const uploadedUrl =
              (Array.isArray(uploadRes) && uploadRes[0]?.url) ||
              uploadRes?.data?.[0]?.url ||
              uploadRes?.url;
            if (!uploadedUrl) {
              setErrorAlert(true);
              return;
            }
            col.attributes.value = uploadedUrl;
          }
        }
      }

      // Lê formValues após uploads para pegar as URLs já substituídas
      const formValues = form.flatMap((row) =>
        row.cols.map((col) => ({ ...col.attributes, inputId: col.inputId })),
      );

      const formColumns = [];
      formValues.forEach((col) => {
        if (IGNORED_INPUT_TYPES.includes(col.inputName)) return;

        // Múltiplas Opções (checkbox, inputId 4): cada opção vira uma entrada separada com "label - true/false"
        if (
          col.inputId === 4 &&
          Array.isArray(col.boxes) &&
          col.boxes.length > 0
        ) {
          const selected = Array.isArray(col.value) ? col.value : [];
          if (selected.length === 0) return;
          formColumns.push({
            attributeId: col.inputName.toLowerCase(),
            responseValue: selected.join(";"),
          });
          return;
        }

        const val = col.value;
        if (val === null || val === undefined || val === "") return;
        if (Array.isArray(val) && val.length === 0) return;
        const attributeId = col.inputName.toLowerCase();
        formColumns.push({
          attributeId: attributeId === "celular" ? "telefone" : attributeId,
          responseValue: String(val),
        });
      });

      const apiData = {
        typeForm: formData.cadastro ? "cadastro" : null, // aqui o typeForm pode ser outro
        eventId: formData.eventId,
        formColumns,
      };

      const res = await createUserResponse({
        formId: formData.id,
        participantId: participant.id,
        data: apiData,
      });

      if (res) {
        setSuccessAlert(true);
      } else {
        setErrorAlert(true);
      }
    } catch (error) {
      console.log({ error });
      setErrorAlert(true);
    }
  }

  // busca respostas já salvas do participante para este formulário
  async function getResponse() {
    try {
      const res = await getUserResponse({
        formId: formData.id,
        participantId: participant.id,
      });

      setSavedResponses(res.total && res.total > 0 ? res.data : []);
    } catch {
      setSavedResponses([]);
    }
  }

  useEffect(() => {
    if (!isOpen) {
      setSavedResponses(undefined);
      setForm([]);
      return;
    }
    getResponse();
  }, [isOpen]);

  // monta o formulário somente após ter as respostas salvas, aplicando-as nos campos
  useEffect(() => {
    if (savedResponses === undefined) return;
    const raw = formData?.json;
    if (!raw) return;
    try {
      const json = typeof raw === "string" ? JSON.parse(raw) : raw;
      if (!Array.isArray(json)) {
        setForm([]);
        return;
      }
      const rows = stripRequired(
        json.filter((row) => row && Array.isArray(row.cols)),
      );

      // inputId → valor do participante (fallback quando não há resposta salva)
      const maskCpf = (value) => {
        if (!value) return value;
        const cpf = String(value)
          .replace(/\D/g, "")
          .match(/(\d{0,3})(\d{0,3})(\d{0,3})(\d{0,2})/);
        return !cpf[2]
          ? cpf[1]
          : `${cpf[1]}.${cpf[2]}${cpf[3] ? `.${cpf[3]}` : ""}${cpf[4] ? `-${cpf[4]}` : ""}`;
      };

      const participantDefaults = {
        13: participant?.nome,
        7: participant?.email,
        9: maskCpf(participant?.cpf),
        8: participant?.telefone,
      };

      const applyValues = (col) => {
        if (IGNORED_INPUT_TYPES.includes(col.attributes.inputName)) return col;
        const isLocked = LOCKED_INPUT_TYPES.includes(col.attributes.inputName);

        if (col.inputId === 4) {
          const match = savedResponses.find(
            (r) =>
              r.attributeId.toLowerCase() ===
              col.attributes.inputName.toLowerCase(),
          );
          if (!match) return col;
          const checkedLabels = match.responseValue
            ? match.responseValue.split(";").map((s) => s.trim())
            : [];
          return {
            ...col,
            attributes: { ...col.attributes, value: checkedLabels },
          };
        }

        const match = savedResponses.find(
          (r) =>
            r.attributeId.toLowerCase() ===
            col.attributes.inputName.toLowerCase(),
        );
        const participantDefault = participantDefaults[col.inputId];
        if (!match && !isLocked && !participantDefault) return col;
        return {
          ...col,
          attributes: {
            ...col.attributes,
            value: match
              ? match.responseValue
              : participantDefault || col.attributes.value,
            disabled: isLocked,
          },
        };
      };

      setForm(rows.map((row) => ({ ...row, cols: row.cols.map(applyValues) })));
    } catch {
      setForm([]);
    }
  }, [formData?.json, savedResponses]);

  return (
    <>
      {successAlert && (
        <SweetAlert
          success
          title="Sucesso"
          confirmBtnText="Ok"
          confirmBtnBsStyle="success"
          onConfirm={() => {
            setSuccessAlert(false);
            toggle();
          }}
        >
          Formulário enviado com sucesso!
        </SweetAlert>
      )}

      {errorAlert && (
        <SweetAlert
          danger
          title="Erro"
          confirmBtnText="Ok"
          confirmBtnBsStyle="danger"
          onConfirm={() => setErrorAlert(false)}
        >
          Ocorreu um erro ao enviar o formulário.
        </SweetAlert>
      )}

      {savedResponses === undefined ? (
        <div className="d-flex justify-content-center py-4">
          <Spinner color="primary" />
        </div>
      ) : (
        <FormMounted
          form={form}
          setForm={setForm}
          onSubmit={handleSubmit}
          hiddenInputIds={[11]}
        />
      )}
    </>
  );
}

function ModalParticipantForm(props) {
  const { isOpen, toggle, participant, eventId } = props;
  // formulários
  const [forms, setForms] = useState([]);
  const [formsLoading, setFormsLoading] = useState(true);
  // índice do formulário aberto no accordion (-1 = nenhum)
  const [openFormIndex, setOpenFormIndex] = useState();

  // alterna o accordion: abre o clicado ou fecha se já estava aberto
  function toggleForm(index) {
    setOpenFormIndex((prev) => (prev === index ? -1 : index));
  }

  // busca todos os formulários do evento
  async function getForms() {
    try {
      const response = await getAllForms({
        eventId: eventId,
        offset: 1,
        limit: 100,
        search: "",
      });

      if (response.status === 200) setForms(response.data.forms || []);
    } catch (error) {
      console.log({ error });
    } finally {
      setFormsLoading(false);
    }
  }

  useEffect(() => {
    getForms();
  }, []);

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="lg">
      <ModalHeader toggle={toggle}>
        Atributos Dinâmicos do Participante
      </ModalHeader>
      <ModalBody>
        {formsLoading ? (
          <div className="d-flex justify-content-center py-4">
            <Spinner color="primary" />
          </div>
        ) : (
          forms.map((formData, index) => (
            <div key={formData.id || index}>
              <SectionComponent
                sectionName={formData.description || `Formulário ${index + 1}`}
                isOpen={openFormIndex === index}
                setIsOpen={() => toggleForm(index)}
              />
              <Collapse isOpen={openFormIndex === index}>
                <div style={{ padding: "16px 0" }}>
                  <FormItem
                    formData={formData}
                    participant={participant}
                    toggle={toggle}
                    isOpen={openFormIndex === index}
                  />
                </div>
              </Collapse>
            </div>
          ))
        )}
      </ModalBody>
    </Modal>
  );
}

export default ModalParticipantForm;
